import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  clearStoredPlaylistSelection,
  dispatchPlaylistStorageSync,
  PLAYLIST_STORAGE_SYNC_EVENT,
  readStoredPlaylistSelection,
  writeStoredPlaylistSelection,
} from '../lib/playlistStorage';

export type PlaylistContextValue = {
  playlistId: string | null;
  playlistName: string;
  setCurrentPlaylist: (id: string | number, name: string) => void;
  clearCurrentPlaylist: () => void;
};

const PlaylistContext = createContext<PlaylistContextValue | null>(null);

export function PlaylistProvider({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth();
  const [playlistId, setPlaylistIdState] = useState<string | null>(() => readStoredPlaylistSelection().id);
  const [playlistName, setPlaylistNameState] = useState<string>(() => readStoredPlaylistSelection().name);
  const prevTokenRef = useRef<string | null>(null);

  const syncFromStorage = useCallback(() => {
    const { id, name } = readStoredPlaylistSelection();
    setPlaylistIdState(id);
    setPlaylistNameState(name);
  }, []);

  useEffect(() => {
    const onSync = () => syncFromStorage();
    window.addEventListener(PLAYLIST_STORAGE_SYNC_EVENT, onSync);
    return () => window.removeEventListener(PLAYLIST_STORAGE_SYNC_EVENT, onSync);
  }, [syncFromStorage]);

  useEffect(() => {
    if (!ready) return;
    const prev = prevTokenRef.current;
    prevTokenRef.current = token;
    if (prev !== null && token === null) {
      setPlaylistIdState(null);
      setPlaylistNameState('');
    }
  }, [token, ready]);

  const setCurrentPlaylist = useCallback((id: string | number, name: string) => {
    const sid = String(id);
    setPlaylistIdState(sid);
    setPlaylistNameState(name);
    writeStoredPlaylistSelection(sid, name);
  }, []);

  const clearCurrentPlaylist = useCallback(() => {
    setPlaylistIdState(null);
    setPlaylistNameState('');
    clearStoredPlaylistSelection();
    dispatchPlaylistStorageSync();
  }, []);

  const value = useMemo<PlaylistContextValue>(
    () => ({
      playlistId,
      playlistName,
      setCurrentPlaylist,
      clearCurrentPlaylist,
    }),
    [playlistId, playlistName, setCurrentPlaylist, clearCurrentPlaylist],
  );

  return <PlaylistContext.Provider value={value}>{children}</PlaylistContext.Provider>;
}

export function usePlaylist(): PlaylistContextValue {
  const ctx = useContext(PlaylistContext);
  if (!ctx) throw new Error('usePlaylist must be used within PlaylistProvider');
  return ctx;
}
